/**
 * PURE. Shared build context of the mesh generator: the resolved spec, the raw skeleton, LOD settings, ids (art §3.2
 * material and region tables) and small helpers.
 */
import type { BoneName } from '../../shared';
import type { CreatureSpec } from '../dna/resolve';
import type { BaseFace, EyeGeom } from '../rig/face';
import type { BodyDims, BuiltRig } from '../rig/skeleton';
import { MeshBuilder, Surface, type V3 } from './builder';

/** Art §3.2 material ids. */
export const MAT = { fur: 0, flesh: 1, sclera: 2, iris: 3, ink: 4, dye0: 5, dye1: 6, tinctA: 7, tinctB: 8, leather: 9, wood: 10, iron: 11, gold: 12, gem: 13,
  parchment: 14, stone: 15, horn: 16, plumage: 17, shell: 18, copper: 19, silver: 20 } as const;
/** Art §3.2 region ids. */
export const REG = { skull: 0, muzzle: 1, ears: 2, neck: 3, torso: 4, upperArm: 5, forearm: 6, thigh: 7, shin: 8, tail: 9, extra: 10 } as const;

export type Lod = 0 | 1 | 'hero';
export interface LodCfg {
  headSides: number; headRings: number; muzzleSides: number; muzzleRings: number; torsoSides: number; limbSides: number; limbRings: number;
  tailSides: number; tailRings: number; eyeSides: number; lidRings: number; teeth: boolean; whiskers: boolean; clothCols: number; clothRows: number;
  browFacets: number; neckSides: number;
}
export const LOD_CFG: Readonly<Record<'0' | '1' | 'hero', LodCfg>> = {
  0: { headSides: 10, headRings: 7, muzzleSides: 8, muzzleRings: 3, torsoSides: 8, limbSides: 6, limbRings: 5, tailSides: 5, tailRings: 10, eyeSides: 8,
    lidRings: 2, teeth: true, whiskers: true, clothCols: 3, clothRows: 4, browFacets: 2, neckSides: 8 },
  1: { headSides: 6, headRings: 5, muzzleSides: 6, muzzleRings: 2, torsoSides: 6, limbSides: 4, limbRings: 3, tailSides: 4, tailRings: 6, eyeSides: 6,
    lidRings: 1, teeth: false, whiskers: false, clothCols: 1, clothRows: 2, browFacets: 2, neckSides: 6 },
  hero: { headSides: 12, headRings: 9, muzzleSides: 8, muzzleRings: 5, torsoSides: 8, limbSides: 6, limbRings: 5, tailSides: 5, tailRings: 10, eyeSides: 16,
    lidRings: 3, teeth: true, whiskers: true, clothCols: 3, clothRows: 4, browFacets: 3, neckSides: 8 },
};

export interface BuildCtx {
  spec: CreatureSpec;
  rig: BuiltRig;
  /** Raw (pre-normalization) pivots. */
  P: Readonly<Partial<Record<BoneName, V3>>>;
  d: BodyDims;
  lod: Lod;
  cfg: LodCfg;
  mb: MeshBuilder;
  /** Bone index by name (throws on a missing bone). */
  bi: (name: BoneName) => number;
  /** Optional bone index (−1 when absent). */
  ob: (name: BoneName) => number;
  /** Head / body skin material (fur or plumage). */
  headMat: number; bodyMat: number;
  face: BaseFace;
  eyes?: [EyeGeom, EyeGeom];
  /** Skull query: radii and centre of the head loft at height y (raw). */
  skull?: (y: number) => { rs: number; rf: number; cz: number };
  /** Front surface z of the skull at (x, y). */
  skullFront?: (x: number, y: number) => number;
  /** Mouth line height and half-width (set by the head builder). */
  mouthY?: number; mouthHalfW?: number;
  /** Muzzle front surface at (x, y) (falls back to the skull). */
  muzzleFront?: (x: number, y: number) => number;
  /** Seed for jitter. */
  seed: number;
}

/** Runs `fn` as a named part on a fresh surface and adds it. */
export function part(ctx: BuildCtx, name: string, fn: (s: Surface) => void): void {
  ctx.mb.beginPart(name);
  const s = new Surface();
  fn(s);
  ctx.mb.add(s);
  ctx.mb.endPart();
}
